import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import activityApi from '../api/activityApi';
import { Profile } from '../../types';

interface ActivityState {
  leaderboard: Profile[];
  leaderboardType: 'liked' | 'rated' | 'roses';
  compatibilityScore: number | null;
}

const initialState: ActivityState = {
  leaderboard: [],
  leaderboardType: 'liked',
  compatibilityScore: null,
};

export const fetchLeaderboard = createAsyncThunk(
  'activity/fetchLeaderboard',
  async (type: 'liked' | 'rated' | 'roses', { rejectWithValue }) => {
    try {
      const res = await activityApi.getLeaderboard(type);
      return { type, profiles: res.data as Profile[] };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load leaderboard');
    }
  },
);

export const fetchCompatibility = createAsyncThunk(
  'activity/fetchCompatibility',
  async (profileId: string, { rejectWithValue }) => {
    try {
      const res = await activityApi.getCompatibility(profileId);
      return res.data as { score: number };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load compatibility');
    }
  },
);

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    clearCompatibility(state) {
      state.compatibilityScore = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaderboard.fulfilled, (state, { payload }) => {
        state.leaderboard = payload.profiles;
        state.leaderboardType = payload.type;
      })
      .addCase(fetchCompatibility.fulfilled, (state, { payload }) => {
        state.compatibilityScore = payload.score;
      });
  },
});

export const { clearCompatibility } = activitySlice.actions;
export default activitySlice.reducer;

export const selectLeaderboard = (state: { activity: ActivityState }) => state.activity.leaderboard;
export const selectLeaderboardType = (state: { activity: ActivityState }) => state.activity.leaderboardType;
export const selectCompatibilityScore = (state: { activity: ActivityState }) => state.activity.compatibilityScore;
